/**
 * The shipped themes and how a theme name is chosen at runtime:
 *   `/api/config.theme` → `import.meta.env.VITE_THEME` → "chairshare".
 * Unknown names fall back to the default (and are reported once) so a typo in
 * config can never produce an unstyled app.
 */
import type { Theme } from "./types.js";
import { chairshare } from "./themes/chairshare.js";
import { neutral } from "./themes/neutral.js";

export const THEMES: Readonly<Record<string, Theme>> = Object.freeze({
  [chairshare.name]: chairshare,
  [neutral.name]: neutral,
});

export const DEFAULT_THEME_NAME = chairshare.name;

export function isThemeName(value: unknown): value is string {
  return typeof value === "string" && Object.hasOwn(THEMES, value);
}

/** First candidate that names a shipped theme, else the default. */
export function resolveThemeName(
  ...candidates: Array<string | null | undefined>
): string {
  for (const c of candidates) {
    const name = typeof c === "string" ? c.trim().toLowerCase() : "";
    if (!name) continue;
    if (isThemeName(name)) return name;
    if (typeof console !== "undefined") {
      console.warn(`[theme] unknown theme "${name}" — falling back to "${DEFAULT_THEME_NAME}"`);
    }
  }
  return DEFAULT_THEME_NAME;
}

export function getTheme(name: string): Theme {
  return THEMES[name] ?? THEMES[DEFAULT_THEME_NAME];
}

/** The build-time theme hint (`VITE_THEME`), if any. */
export function buildTimeThemeName(): string | undefined {
  const v = import.meta.env?.VITE_THEME;
  return typeof v === "string" && v.trim() ? v.trim() : undefined;
}
